import type * as Monaco from "monaco-editor";
import { copyTextToClipboard } from "../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import { ICodeEditorProblem, readModelProblems } from "./code-editor-problems";
import type { ICodeEditorCursor } from "./code-editor-toolbar";

/** Everything the toolbar draws about the editor's state. */
export interface ICodeEditorControlsState {
  fontSize: number;
  isWrapEnabled: boolean;
  cursor: ICodeEditorCursor | null;
  problems: ICodeEditorProblem[];
  formatCompleted: boolean;
}

/** The props `CodeEditorToolbar` needs, minus the ones only the host can supply. */
export interface ICodeEditorToolbarBinding extends ICodeEditorControlsState {
  onUndo: () => void;
  onRedo: () => void;
  onCopy: () => Promise<void>;
  onPasteAtCursor: () => Promise<void>;
  onToggleWrap: () => void;
  onFontSizeChange: (fontSize: number) => void;
}

export interface ICodeEditorControlsOptions {
  /** Fired after every change, so the host can re-render with the new state. */
  onChange: (state: ICodeEditorControlsState) => void;
  fontSize?: number;
  /** Whether the editor mounts with wrapping on. */
  isWrapEnabled?: boolean;
}

const DEFAULT_FONT_SIZE: number = 12;
/** How long the tick stays beside TIDY UP after a format. */
const FORMAT_TICK_MS: number = 3000;

/**
 * The toolbar's half of a code editor: undo, redo, copy, paste at the caret, wrapping, the font
 * stepper, the caret position and the language server's markers.
 *
 * Every editor in the designer needs the same wiring, and it is all imperative — Monaco's own
 * commands and disposables — so it lives here once instead of eighty lines per host. Modelled on
 * `AiPendingEdit`: the host constructs one, attaches it in `onMount`, disposes it on unmount, and
 * re-renders from `onChange`.
 */
export class CodeEditorControls {
  private editor: Monaco.editor.IStandaloneCodeEditor | undefined;
  private monaco: typeof Monaco | undefined;
  private disposables: Monaco.IDisposable[] = [];
  private formatTimer: ReturnType<typeof setTimeout> | undefined;
  /** Where a paste lands: the last line the caret was put on, not wherever focus went. */
  private lastLineNumber: number = 1;
  private readonly options: ICodeEditorControlsOptions;

  state: ICodeEditorControlsState;

  constructor(options: ICodeEditorControlsOptions) {
    this.options = options;
    this.state = {
      fontSize: options.fontSize ?? DEFAULT_FONT_SIZE,
      isWrapEnabled: options.isWrapEnabled ?? true,
      cursor: null,
      problems: [],
      formatCompleted: false
    };
  }

  /** Call from the editor's `onMount`; `monaco` is what `beforeMount` handed over. */
  attach = (monacoInstance: typeof Monaco | undefined, editor: Monaco.editor.IStandaloneCodeEditor): void => {
    this.editor = editor;
    this.monaco = monacoInstance;

    editor.updateOptions({
      fontSize: this.state.fontSize,
      wordWrap: this.state.isWrapEnabled ? "on" : "off"
    });

    this.disposables.push(
      editor.onDidChangeCursorPosition((event: Monaco.editor.ICursorPositionChangedEvent): void => {
        this.lastLineNumber = event.position.lineNumber;
        this.publish({ cursor: { lineNumber: event.position.lineNumber, column: event.position.column } });
      })
    );

    // Markers arrive after the language server has had its say, which is never on mount.
    if (monacoInstance) {
      this.disposables.push(monacoInstance.editor.onDidChangeMarkers((): void => this.refreshProblems()));
      this.refreshProblems();
    }
  };

  dispose = (): void => {
    for (const disposable of this.disposables) disposable.dispose();
    this.disposables = [];
    if (this.formatTimer) clearTimeout(this.formatTimer);
    this.editor = undefined;
    this.monaco = undefined;
  };

  undo = (): void => {
    this.editor?.trigger("toolbar", "undo", null);
  };

  redo = (): void => {
    this.editor?.trigger("toolbar", "redo", null);
  };

  /** The selection when there is one, the whole file otherwise. */
  copy = async (): Promise<void> => {
    const model: Monaco.editor.ITextModel | null | undefined = this.editor?.getModel();
    if (!this.editor || !model) return;

    const selection: Monaco.Selection | null = this.editor.getSelection();
    const hasSelection: boolean = !!selection && !selection.isEmpty();
    const text: string = hasSelection && selection ? model.getValueInRange(selection) : model.getValue();

    // The shared write: the Clipboard API, then the textarea fallback when permission is refused
    // (which also hands the focus back to the editor instead of leaving it on <body>).
    await copyTextToClipboard(text);
  };

  pasteAtCursor = async (): Promise<void> => {
    if (!this.editor || !this.monaco) return;

    let text: string = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      return;
    }
    if (!text) return;

    const line: number = this.lastLineNumber;
    const column: number = this.editor.getModel()?.getLineMaxColumn(line) ?? 1;
    this.editor.executeEdits("toolbar", [
      { range: new this.monaco.Range(line, column, line, column), text: `\n${text}`, forceMoveMarkers: true }
    ]);
    this.editor.focus();
  };

  toggleWrap = (): void => {
    const isWrapEnabled: boolean = !this.state.isWrapEnabled;
    this.editor?.updateOptions({ wordWrap: isWrapEnabled ? "on" : "off" });
    this.publish({ isWrapEnabled });
  };

  setFontSize = (fontSize: number): void => {
    this.editor?.updateOptions({ fontSize });
    this.publish({ fontSize });
  };

  /** Shows the tick beside TIDY UP, then takes it away again. */
  markFormatted = (): void => {
    if (this.formatTimer) clearTimeout(this.formatTimer);
    this.publish({ formatCompleted: true });
    this.formatTimer = setTimeout((): void => this.publish({ formatCompleted: false }), FORMAT_TICK_MS);
  };

  refreshProblems = (): void => {
    this.publish({ problems: readModelProblems(this.monaco, this.editor?.getModel()) });
  };

  /** Spread onto `CodeEditorToolbar`; the host adds only what is its own. */
  get toolbarBinding(): ICodeEditorToolbarBinding {
    return {
      ...this.state,
      onUndo: this.undo,
      onRedo: this.redo,
      onCopy: this.copy,
      onPasteAtCursor: this.pasteAtCursor,
      onToggleWrap: this.toggleWrap,
      onFontSizeChange: this.setFontSize
    };
  }

  private publish = (change: Partial<ICodeEditorControlsState>): void => {
    this.state = { ...this.state, ...change };
    this.options.onChange(this.state);
  };
}
